import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { APP_CONFIG } from '../config/app-config.value';

/** Un profil de test exposé par `GET /api/dev/profiles` (devbar, poste de développement). */
export interface DevProfileOption {
  readonly key: string;
  readonly label: string;
  readonly description: string;
}

interface DevProfilesResponse {
  readonly cookieName: string;
  readonly current: string;
  readonly available: readonly DevProfileOption[];
}

/**
 * Devbar de développement — mode branché uniquement. Interroge `GET /api/dev/profiles`, présent
 * seulement quand le DevBypass du backend est actif (poste de dev) et **absent en recette/production**
 * (l'endpoint n'y est pas mappé) : le devbar disparaît donc tout seul en vrai SSO.
 *
 * Basculer un profil = poser le cookie du backend puis recharger. `DevelopmentCurrentUser` relit le
 * cookie et applique les droits du profil, ce que `/api/me` (navigation, actions) **et**
 * `[RequirePermission]` (barrière serveur) reflètent aussitôt — on teste les vrais périmètres contre
 * le backend réel, à la manière du devbar d'UniApp.
 */
@Injectable({ providedIn: 'root' })
export class DevProfileStore {
  private readonly http = inject(HttpClient);
  private readonly live = APP_CONFIG.dataSource === 'api';

  private readonly _data = signal<DevProfilesResponse | null>(null);

  readonly available = computed<readonly DevProfileOption[]>(() => this._data()?.available ?? []);
  readonly current = computed<string | null>(() => this._data()?.current ?? null);

  /** Vrai si le devbar doit s'afficher : mode branché ET endpoint présent (DevBypass local). */
  readonly enabled = computed(() => this._data() !== null);

  constructor() {
    if (this.live) {
      void this.load();
    }
  }

  private async load(): Promise<void> {
    try {
      const data = await firstValueFrom(
        this.http.get<DevProfilesResponse>(`${APP_CONFIG.apiBaseUrl}/dev/profiles`),
      );
      this._data.set(data);
    } catch {
      // Endpoint absent (recette/production) ou injoignable : pas de devbar, comportement normal.
      this._data.set(null);
    }
  }

  /**
   * Bascule de profil : pose le cookie (même origine via le proxy `/api`, donc lisible par le
   * backend) puis recharge pour que toute l'app reparte avec les droits du profil choisi.
   */
  switchTo(key: string): void {
    const cookieName = this._data()?.cookieName ?? 'uniit_dev_profile';
    document.cookie = `${cookieName}=${encodeURIComponent(key)}; path=/; samesite=lax`;
    window.location.reload();
  }
}
